import React from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  ArrowRight,
  Bot,
  CreditCard,
  RefreshCw,
} from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Button } from "@/components/ui/button";

const SuperAdminPage = () => {
  return (
    <MainLayout>
      <SuperAdminLayout
        title="Super Admin"
        description="Manage system-wide settings and configurations"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="flex flex-col h-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Dashboard</CardTitle>
                  <CardDescription>
                    View system analytics and metrics
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-3xl font-bold">167</p>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-3xl font-bold">24</p>
                  <p className="text-sm text-muted-foreground">Organizations</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-3xl font-bold">35</p>
                  <p className="text-sm text-muted-foreground">Agents</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-3xl font-bold">412</p>
                  <p className="text-sm text-muted-foreground">Conversations</p>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline" className="w-full">
                <Link
                  to="/super-admin/dashboard"
                  className="flex items-center justify-center gap-2"
                >
                  View Detailed Dashboard
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          <Card className="flex flex-col h-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Organizations</CardTitle>
                  <CardDescription>
                    Monitor organization signups and status
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <p className="mb-4">
                View and manage all organizations that have signed up since June
                16, 2025. Monitor onboarding progress, subscription status, and
                user details.
              </p>
              <div className="flex gap-3 flex-wrap">
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  Active Subscriptions
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  Onboarding Progress
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                  Agent Analytics
                </span>
              </div>
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline" className="w-full">
                <Link
                  to="/super-admin/organizations"
                  className="flex items-center justify-center gap-2"
                >
                  Monitor Organizations
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          <Card className="flex flex-col h-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Template Manager</CardTitle>
                  <CardDescription>
                    Manage agent templates and categories
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <p className="mb-4">
                Manage the templates that organizations can use to create
                agents. Create, edit, and organize templates into categories.
              </p>
              <div className="flex gap-3 flex-wrap">
                <span className="px-3 py-1 bg-muted rounded-full text-sm">
                  Legal Assistant
                </span>
                <span className="px-3 py-1 bg-muted rounded-full text-sm">
                  Customer Support
                </span>
                <span className="px-3 py-1 bg-muted rounded-full text-sm">
                  Sales Agent
                </span>
                <span className="px-3 py-1 bg-muted rounded-full text-sm">
                  Intake Specialist
                </span>
                <span className="px-3 py-1 bg-muted rounded-full text-sm">
                  Financial Advisor
                </span>
              </div>
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline" className="w-full">
                <Link
                  to="/super-admin/templates"
                  className="flex items-center justify-center gap-2"
                >
                  Manage Templates
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          <Card className="flex flex-col h-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Plan Management</CardTitle>
                  <CardDescription>
                    Manage subscription plans and features
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-3xl font-bold">4</p>
                  <p className="text-sm text-muted-foreground">Active Plans</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-3xl font-bold">$259</p>
                  <p className="text-sm text-muted-foreground">
                    Avg Plan Price
                  </p>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  Starter: $69
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  Business: $259
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                  Enterprise: $499
                </span>
              </div>
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline" className="w-full">
                <Link
                  to="/super-admin/plans"
                  className="flex items-center justify-center gap-2"
                >
                  Manage Plans
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          <Card className="flex flex-col h-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Bot className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>AI Assistants</CardTitle>
                  <CardDescription>
                    Manage system AI assistants for generation tasks
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <p className="mb-4">
                Create and manage AI assistants that generate content like
                emails, social media posts, and documents. Configure form fields
                and output schemas.
              </p>
              <div className="flex gap-3 flex-wrap">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  Marketing
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  Sales
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                  Work
                </span>
              </div>
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline" className="w-full">
                <Link
                  to="/super-admin/ai-assistants"
                  className="flex items-center justify-center gap-2"
                >
                  Manage AI Assistants
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          <Card className="flex flex-col h-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Stripe Data Sync</CardTitle>
                  <CardDescription>
                    Sync Stripe data to your database
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <p className="mb-4">
                Set up automatic syncing of Stripe customers, subscriptions,
                products, and invoices to your Postgres database for better
                analytics and reporting.
              </p>
              <div className="flex gap-3 flex-wrap">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  Real-time Webhooks
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  Historical Backfill
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                  Automatic Sync
                </span>
              </div>
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline" className="w-full">
                <Link
                  to="/super-admin/stripe-sync"
                  className="flex items-center justify-center gap-2"
                >
                  Configure Stripe Sync
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default SuperAdminPage;
